import React, { useState, useEffect, useMemo } from "react";
import {
  CalendarDays,
  MapPin,
  Users,
  Phone,
  Mail,
  Send,
  PartyPopper,
  Search,
  FileText,
  Navigation,
  CheckCircle2,
  Loader2,
  ExternalLink,
  LocateFixed,
} from "lucide-react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";

const EVENT_TYPES = [
  "Birthday",
  "Wedding",
  "Engagement",
  "Corporate",
  "School / College",
  "Party",
  "Other",
];

const INITIAL_FORM = {
  name: "",
  phone: "",
  email: "",

  event_type: "Birthday",
  event_date: "",
  event_time: "",

  location: "",
  guests: "",

  service_type: "",
  staff_required: "",

  requirements: "",

  latitude: null,
  longitude: null,
  accuracy: null,

  pincode: "",
  map_link: "",
};

export default function Catering() {
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [locationLoading, setLocationLoading] = useState(false);
  const [checkingQuotation, setCheckingQuotation] = useState(false);

  const [locationDetected, setLocationDetected] = useState(false);
  const [locationError, setLocationError] = useState("");

  const [quotation, setQuotation] = useState(null);
  const [quotationPhone, setQuotationPhone] = useState("");

  const [form, setForm] = useState(INITIAL_FORM);

  // =====================================================
  // UPDATE FORM
  // =====================================================

  const update = (e) => {
    const { name, value } = e.target;

    setForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // =====================================================
  // REVERSE GEOCODING
  // =====================================================

  const getAddressFromCoordinates = async (
    latitude,
    longitude
  ) => {
    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/reverse?format=jsonv2&lat=${latitude}&lon=${longitude}&addressdetails=1`,
        {
          headers: {
            Accept: "application/json",
          },
        }
      );

      if (!response.ok) {
        throw new Error("Unable to find address.");
      }

      const data = await response.json();

      const address = data.address || {};

      return {
        displayName: data.display_name || "",
        pincode:
          address.postcode ||
          address.pincode ||
          "",
      };
    } catch (error) {
      console.error(
        "Reverse geocoding error:",
        error
      );

      return {
        displayName: "",
        pincode: "",
      };
    }
  };

  // =====================================================
  // DETECT CURRENT LOCATION
  // =====================================================

  const detectLocation = () => {
    if (!navigator.geolocation) {
      toast.error(
        "Location is not supported by this browser."
      );
      return;
    }

    setLocationLoading(true);
    setLocationError("");

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        try {
          const latitude =
            position.coords.latitude;

          const longitude =
            position.coords.longitude;

          const accuracy =
            position.coords.accuracy;

          const mapLink = `https://www.google.com/maps?q=${latitude},${longitude}`;

          const addressData =
            await getAddressFromCoordinates(
              latitude,
              longitude
            );

          setForm((prev) => ({
            ...prev,

            latitude,
            longitude,
            accuracy,

            map_link: mapLink,

            location:
              prev.location.trim() !== ""
                ? prev.location
                : addressData.displayName,

            pincode:
              prev.pincode.trim() !== ""
                ? prev.pincode
                : addressData.pincode,
          }));

          setLocationDetected(true);

          toast.success(
            "Current location detected successfully!"
          );
        } catch (error) {
          console.error(error);

          const message =
            error?.message ||
            "Unable to get your current location.";

          setLocationError(message);

          toast.error(message);
        } finally {
          setLocationLoading(false);
        }
      },

      (error) => {
        console.error(
          "Geolocation error:",
          error
        );

        let message =
          "Unable to access your location.";

        if (error.code === 1) {
          message =
            "Location permission was denied. Please allow location access.";
        } else if (error.code === 2) {
          message =
            "Your location could not be determined.";
        } else if (error.code === 3) {
          message =
            "Location request timed out. Please try again.";
        }

        setLocationError(message);

        toast.error(message);

        setLocationLoading(false);
      },

      {
        enableHighAccuracy: true,
        timeout: 15000,
        maximumAge: 0,
      }
    );
  };

  // =====================================================
  // SUBMIT CATERING ENQUIRY
  // =====================================================

  const submit = async (e) => {
    e.preventDefault();

    // -----------------------------
    // REQUIRED VALIDATION
    // -----------------------------

    if (!form.name.trim()) {
      toast.error("Please enter your name.");
      return;
    }

    if (!form.phone.trim()) {
      toast.error(
        "Please enter your phone number."
      );
      return;
    }

    if (!form.event_date) {
      toast.error(
        "Please select the event date."
      );
      return;
    }

    if (!form.service_type) {
      toast.error(
        "Please select a catering service."
      );
      return;
    }

    if (!form.staff_required) {
      toast.error(
        "Please select whether you need staff at the event."
      );
      return;
    }

    if (!form.guests) {
      toast.error(
        "Please enter the number of guests."
      );
      return;
    }

    if (Number(form.guests) < 1) {
      toast.error(
        "Number of guests must be at least 1."
      );
      return;
    }

    if (!form.location.trim()) {
      toast.error(
        "Please enter the event location."
      );
      return;
    }

    // SPECIAL REQUIREMENTS IS MANDATORY
    if (!form.requirements.trim()) {
      toast.error(
        "Please enter your special requirements."
      );
      return;
    }

    try {
      setLoading(true);

      const response = await api.post(
        "/catering/enquiries",
        {
          ...form,

          guests: Number(form.guests),

          latitude:
            form.latitude !== null
              ? Number(form.latitude)
              : null,

          longitude:
            form.longitude !== null
              ? Number(form.longitude)
              : null,

          accuracy:
            form.accuracy !== null
              ? Number(form.accuracy)
              : null,
        }
      );

      const enquiryId =
        response?.data?.id;

      toast.success(
        "Catering enquiry submitted successfully!"
      );

      // Reset form
      setForm({
        ...INITIAL_FORM,
      });

      setLocationDetected(false);
      setLocationError("");

      // Go to tracking
      if (enquiryId) {
        setTimeout(() => {
          navigate(
            `/catering/tracking/${enquiryId}`
          );
        }, 700);
      }
    } catch (error) {
      console.error(
        "Catering submission error:",
        error
      );

      toast.error(
        error?.response?.data?.detail ||
          "Unable to submit enquiry. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  // =====================================================
  // CHECK QUOTATION
  // =====================================================

  const checkQuotation = async (e) => {
    e.preventDefault();

    const phone =
      quotationPhone.trim();

    if (!phone) {
      toast.error(
        "Please enter your phone number."
      );
      return;
    }

    try {
      setCheckingQuotation(true);
      setQuotation(null);

      const response = await api.get(
        `/catering/quotation-by-phone/${encodeURIComponent(
          phone
        )}`
      );

      setQuotation(response.data);

      toast.success(
        "Quotation found!"
      );
    } catch (error) {
      console.error(
        "Quotation error:",
        error
      );

      setQuotation(null);

      toast.error(
        error?.response?.data?.detail ||
          "Quotation not found. Please check your phone number."
      );
    } finally {
      setCheckingQuotation(false);
    }
  };

  // =====================================================
  // OPEN QUOTATION
  // =====================================================

  const openQuotation = () => {
    if (!quotation?.id) {
      toast.error(
        "Quotation ID not available."
      );
      return;
    }

    navigate(
      `/catering/quotation/${quotation.id}`
    );
  };

  // =====================================================
  // QUOTATION AMOUNTS
  // =====================================================

  const totalAmount = Number(
    quotation?.quotation?.total_amount || 0
  );

  const advanceAmount = Number(
    quotation?.quotation?.advance_amount || 0
  );

  const balanceAmount = Number(
    quotation?.quotation?.balance_amount || 0
  );

  // =====================================================
  // UI
  // =====================================================

  return (
    <div className="min-h-screen">

      {/* =================================================
          HERO
      ================================================= */}

      <section className="max-w-7xl mx-auto px-4 md:px-12 pt-12 md:pt-20 pb-12">

        <div className="grid lg:grid-cols-2 gap-10 items-center">

          <motion.div
            initial={{
              opacity: 0,
              y: 25,
            }}
            animate={{
              opacity: 1,
              y: 0,
            }}
            transition={{
              duration: 0.6,
            }}
          >

            <div className="inline-flex items-center gap-2 glass rounded-full px-4 py-2 text-xs uppercase tracking-widest text-[#B8E14A] mb-5">

              <PartyPopper className="w-4 h-4" />

              Catering & Events

            </div>

            <h1 className="font-heading text-5xl md:text-7xl leading-[0.95] tracking-tight">

              Make your event

              <span className="block text-[#FF69B4]">
                extra sweet.
              </span>

            </h1>

            <p className="mt-6 text-[#E6F4F2]/75 text-base md:text-lg max-w-xl leading-relaxed">

              From intimate birthdays to grand
              weddings and corporate events,
              Ice Cream Paradise By WOOP brings
              handcrafted ice cream, toppings,
              live counters and dessert
              experiences to your event.

            </p>

            <div className="grid grid-cols-2 gap-3 mt-8 max-w-lg">

              {[
                "Live Ice Cream Counter",
                "Bulk Ice Cream Supply",
                "Dessert Stations",
                "Custom Events",
              ].map((item) => (

                <div
                  key={item}
                  className="glass rounded-2xl px-4 py-3 text-sm text-[#E6F4F2]"
                >
                  🍨 {item}
                </div>

              ))}

            </div>

          </motion.div>

          <motion.div
            initial={{
              opacity: 0,
              scale: 0.96,
            }}
            animate={{
              opacity: 1,
              scale: 1,
            }}
            transition={{
              duration: 0.6,
              delay: 0.1,
            }}
            className="glass rounded-[2rem] p-5 md:p-8"
          >

            <div className="aspect-[4/3] rounded-[1.5rem] overflow-hidden bg-[#1F5F5C] flex items-center justify-center">

              <div className="text-center px-6">

                <div className="text-7xl mb-5">
                  🍦
                </div>

                <h2 className="font-heading text-3xl">
                  Catering for every occasion
                </h2>

                <p className="text-sm text-white/65 mt-3">

                  Tell us about your event and
                  we'll create the perfect dessert
                  experience for you.

                </p>

              </div>

            </div>

          </motion.div>

        </div>

      </section>

      {/* =================================================
          OCCASIONS
      ================================================= */}

      <section className="max-w-7xl mx-auto px-4 md:px-12 py-10">

        <div className="text-center mb-8">

          <div className="text-[#B8E14A] text-xs uppercase tracking-widest">
            Perfect for
          </div>

          <h2 className="font-heading text-4xl md:text-5xl mt-2">
            Every kind of celebration
          </h2>

        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">

          {[
            ["🎂", "Birthdays"],
            ["💍", "Weddings"],
            ["💼", "Corporate"],
            ["🎉", "Parties"],
          ].map(([emoji, title]) => (

            <div
              key={title}
              className="glass rounded-3xl p-6 text-center"
            >

              <div className="text-4xl mb-3">
                {emoji}
              </div>

              <div className="font-heading text-xl">
                {title}
              </div>

            </div>

          ))}

        </div>

      </section>

      {/* =================================================
          CATERING FORM
      ================================================= */}

      <section className="max-w-5xl mx-auto px-4 md:px-12 py-16">

        <div className="glass rounded-[2rem] p-5 md:p-10">

          <div className="text-center mb-10">

            <div className="text-[#B8E14A] text-xs uppercase tracking-widest">
              Get a quotation
            </div>

            <h2 className="font-heading text-4xl md:text-5xl mt-2">
              Tell us about your event
            </h2>

            <p className="text-white/60 mt-3 max-w-2xl mx-auto">

              Share your event details and our
              team will prepare a suitable
              catering quotation for you.

            </p>

          </div>

          <form
            onSubmit={submit}
            className="space-y-6"
          >

            {/* =================================================
                CONTACT DETAILS
            ================================================= */}

            <div className="grid md:grid-cols-2 gap-5">

              <Field
                label="Your Name *"
                name="name"
                value={form.name}
                onChange={update}
                placeholder="Enter your name"
              />

              <Field
                label="Phone Number *"
                name="phone"
                type="tel"
                value={form.phone}
                onChange={update}
                placeholder="+91 XXXXX XXXXX"
                icon={
                  <Phone className="w-4 h-4" />
                }
              />

              <Field
                label="Email"
                name="email"
                type="email"
                value={form.email}
                onChange={update}
                placeholder="your@email.com"
                icon={
                  <Mail className="w-4 h-4" />
                }
              />

              {/* EVENT TYPE */}

              <div>

                <label className="block text-xs uppercase tracking-widest text-white/60 mb-2">
                  Event Type *
                </label>

                <select
                  name="event_type"
                  value={form.event_type}
                  onChange={update}
                  className="w-full rounded-2xl bg-white/10 border border-white/10 px-4 py-3.5 text-white outline-none focus:border-[#B8E14A]"
                >

                  {EVENT_TYPES.map(
                    (type) => (

                      <option
                        key={type}
                        value={type}
                        className="text-black"
                      >
                        {type}
                      </option>

                    )
                  )}

                </select>

              </div>

            </div>

            {/* =================================================
                EVENT DETAILS
            ================================================= */}

            <div className="pt-6 border-t border-white/10">

              <h3 className="font-heading text-2xl mb-5">
                Event Details
              </h3>

              <div className="grid md:grid-cols-2 gap-5">

                <Field
                  label="Event Date *"
                  name="event_date"
                  type="date"
                  value={form.event_date}
                  onChange={update}
                  icon={
                    <CalendarDays className="w-4 h-4" />
                  }
                />

                <Field
                  label="Event Time"
                  name="event_time"
                  type="time"
                  value={form.event_time}
                  onChange={update}
                />

                <Field
                  label="Number of Guests *"
                  name="guests"
                  type="number"
                  min="1"
                  value={form.guests}
                  onChange={update}
                  placeholder="Example: 150"
                  icon={
                    <Users className="w-4 h-4" />
                  }
                />

              </div>

              {/* =================================================
                  CATERING SERVICE
              ================================================= */}

              <div className="mt-6">

                <label className="block text-xs uppercase tracking-widest text-white/60 mb-3">
                  Catering Service *
                </label>

                <div className="grid md:grid-cols-2 gap-4">

                  <button
                    type="button"
                    onClick={() =>
                      setForm((prev) => ({
                        ...prev,
                        service_type:
                          "Delivery",
                      }))
                    }
                    className={`rounded-2xl p-5 text-left border-2 transition ${
                      form.service_type ===
                      "Delivery"
                        ? "border-[#B8E14A] bg-[#B8E14A]/15"
                        : "border-white/10 bg-white/5"
                    }`}
                  >

                    <div className="text-2xl mb-2">
                      🚚
                    </div>

                    <div className="font-semibold">
                      Delivery / Bulk Supply
                    </div>

                    <div className="text-xs text-white/50 mt-1">
                      We deliver the ice cream
                      and items to your event.
                    </div>

                  </button>

                  <button
                    type="button"
                    onClick={() =>
                      setForm((prev) => ({
                        ...prev,
                        service_type:
                          "Pickup",
                      }))
                    }
                    className={`rounded-2xl p-5 text-left border-2 transition ${
                      form.service_type ===
                      "Pickup"
                        ? "border-[#B8E14A] bg-[#B8E14A]/15"
                        : "border-white/10 bg-white/5"
                    }`}
                  >

                    <div className="text-2xl mb-2">
                      🏪
                    </div>

                    <div className="font-semibold">
                      Customer Pickup
                    </div>

                    <div className="text-xs text-white/50 mt-1">
                      I will collect the ice cream
                      from your shop.
                    </div>

                  </button>

                </div>

              </div>

              {/* =================================================
                  STAFF REQUIREMENT
              ================================================= */}

              <div className="mt-6">

                <label className="block text-xs uppercase tracking-widest text-white/60 mb-3">
                  Do you need staff at the event? *
                </label>

                <div className="grid grid-cols-2 gap-4">

                  <button
                    type="button"
                    onClick={() =>
                      setForm((prev) => ({
                        ...prev,
                        staff_required:
                          "Yes",
                      }))
                    }
                    className={`rounded-2xl p-5 text-left border-2 transition ${
                      form.staff_required ===
                      "Yes"
                        ? "border-[#B8E14A] bg-[#B8E14A]/15"
                        : "border-white/10 bg-white/5"
                    }`}
                  >

                    <div className="text-2xl mb-2">
                      👨‍🍳
                    </div>

                    <div className="font-semibold">
                      Yes, I need staff
                    </div>

                    <div className="text-xs text-white/50 mt-1">
                      Staff / live counter
                      service at my event.
                    </div>

                  </button>

                  <button
                    type="button"
                    onClick={() =>
                      setForm((prev) => ({
                        ...prev,
                        staff_required:
                          "No",
                      }))
                    }
                    className={`rounded-2xl p-5 text-left border-2 transition ${
                      form.staff_required ===
                      "No"
                        ? "border-[#B8E14A] bg-[#B8E14A]/15"
                        : "border-white/10 bg-white/5"
                    }`}
                  >

                    <div className="text-2xl mb-2">
                      📦
                    </div>

                    <div className="font-semibold">
                      No, delivery only
                    </div>

                    <div className="text-xs text-white/50 mt-1">
                      I only need the ice cream
                      supplied.
                    </div>

                  </button>

                </div>

              </div>

            </div>

            {/* =================================================
                LOCATION
            ================================================= */}

            <div className="pt-6 border-t border-white/10">

              <h3 className="font-heading text-2xl mb-5">
                Event Location
              </h3>

              <div className="rounded-3xl bg-white/5 border border-white/10 p-5">

                {/* GPS BUTTON */}

                <button
                  type="button"
                  onClick={detectLocation}
                  disabled={locationLoading}
                  className="w-full btn-primary justify-center py-4 disabled:opacity-50"
                >

                  {locationLoading ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />

                      Detecting Current Location...
                    </>
                  ) : locationDetected ? (
                    <>
                      <CheckCircle2 className="w-5 h-5" />

                      Current Location Detected
                    </>
                  ) : (
                    <>
                      <Navigation className="w-5 h-5" />

                      Use My Current Location
                    </>
                  )}

                </button>

                {/* GPS DETAILS */}

                {locationDetected && (
                  <div className="mt-5 rounded-2xl border border-[#B8E14A]/20 bg-[#B8E14A]/5 p-5">

                    <div className="flex items-center gap-2 mb-4">

                      <LocateFixed className="w-5 h-5 text-[#B8E14A]" />

                      <div className="font-semibold">
                        Location Coordinates
                      </div>

                    </div>

                    <div className="grid md:grid-cols-3 gap-3">

                      <CoordinateBox
                        label="Latitude"
                        value={
                          form.latitude !== null
                            ? form.latitude.toFixed(
                                6
                              )
                            : "-"
                        }
                      />

                      <CoordinateBox
                        label="Longitude"
                        value={
                          form.longitude !== null
                            ? form.longitude.toFixed(
                                6
                              )
                            : "-"
                        }
                      />

                      <CoordinateBox
                        label="Accuracy"
                        value={
                          form.accuracy !== null
                            ? `${Math.round(
                                form.accuracy
                              )} m`
                            : "-"
                        }
                      />

                    </div>

                    {form.map_link && (
                      <a
                        href={form.map_link}
                        target="_blank"
                        rel="noreferrer"
                        className="mt-4 inline-flex items-center gap-2 text-sm text-[#B8E14A] hover:underline"
                      >

                        <MapPin className="w-4 h-4" />

                        View detected location on Google Maps

                        <ExternalLink className="w-3.5 h-3.5" />

                      </a>
                    )}

                  </div>
                )}

                <div className="text-center text-xs text-white/40 my-5">
                  OR ENTER EVENT ADDRESS MANUALLY
                </div>

                {/* ADDRESS */}

                <div>

                  <label className="block text-xs uppercase tracking-widest text-white/60 mb-2">
                    Full Event Address *
                  </label>

                  <div className="relative">

                    <MapPin className="absolute left-4 top-4 w-5 h-5 text-white/50" />

                    <textarea
                      name="location"
                      value={form.location}
                      onChange={update}
                      rows={4}
                      placeholder="House No, Street, Area, Landmark, City, Pincode..."
                      className="w-full rounded-2xl bg-white/10 border border-white/10 px-4 py-4 pl-12 text-white placeholder:text-white/35 outline-none focus:border-[#B8E14A] resize-none"
                    />

                  </div>

                  <p className="text-xs text-white/40 mt-2">
                    Enter the exact place where
                    the catering event will happen.
                  </p>

                </div>

                {/* PINCODE */}

                <div className="mt-4">

                  <label className="block text-xs uppercase tracking-widest text-white/60 mb-2">
                    Pincode
                  </label>

                  <input
                    type="text"
                    name="pincode"
                    value={form.pincode}
                    onChange={update}
                    placeholder="Enter pincode"
                    className="w-full rounded-2xl bg-white/10 border border-white/10 px-4 py-3.5 text-white placeholder:text-white/35 outline-none focus:border-[#B8E14A]"
                  />

                </div>

                {/* LOCATION ERROR */}

                {locationError && (
                  <div className="mt-4 rounded-2xl border border-red-400/20 bg-red-400/10 p-4 text-sm text-red-200">
                    {locationError}
                  </div>
                )}

              </div>

            </div>

            {/* =================================================
                SPECIAL REQUIREMENTS
            ================================================= */}

            <div>

              <label className="block text-xs uppercase tracking-widest text-white/60 mb-2">
                Special Requirements *
              </label>

              <textarea
                name="requirements"
                value={form.requirements}
                onChange={update}
                rows={5}
                required
                placeholder="Tell us about flavours, toppings, live counter, staff, tables, freezer requirements, quantity preferences, or anything else you need..."
                className="w-full rounded-2xl bg-white/10 border border-white/10 px-4 py-4 text-white placeholder:text-white/35 outline-none focus:border-[#B8E14A] resize-none"
              />

              <p className="text-xs text-white/40 mt-2">
                Please describe your requirements so
                our team can prepare an accurate quotation.
              </p>

            </div>

            {/* =================================================
                SUBMIT
            ================================================= */}

            <button
              type="submit"
              disabled={loading}
              className="btn-primary w-full justify-center py-4 text-base disabled:opacity-50"
            >

              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />

                  Submitting...
                </>
              ) : (
                <>
                  <Send className="w-4 h-4" />

                  Request Catering Quote
                </>
              )}

            </button>

            <p className="text-center text-xs text-white/45">

              No payment is required at this stage.
              We'll contact you with your quotation.

            </p>

          </form>

        </div>

      </section>

      {/* =================================================
          CHECK QUOTATION
      ================================================= */}

      <section className="max-w-5xl mx-auto px-4 md:px-12 pb-20">

        <div className="glass rounded-[2rem] p-5 md:p-10">

          <div className="text-center mb-8">

            <div className="text-[#B8E14A] text-xs uppercase tracking-widest">
              Existing enquiry
            </div>

            <h2 className="font-heading text-4xl md:text-5xl mt-2">
              Check My Quotation
            </h2>

            <p className="text-white/60 mt-3 max-w-2xl mx-auto">

              Already requested catering?
              Enter the same phone number you
              used when submitting your enquiry.

            </p>

          </div>

          <form
            onSubmit={checkQuotation}
            className="max-w-xl mx-auto"
          >

            <div className="relative">

              <Phone className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-white/50" />

              <input
                type="tel"
                value={quotationPhone}
                onChange={(e) =>
                  setQuotationPhone(
                    e.target.value
                  )
                }
                placeholder="Enter your phone number"
                className="w-full rounded-2xl bg-white/10 border border-white/10 px-4 py-4 pl-12 text-white placeholder:text-white/35 outline-none focus:border-[#B8E14A]"
              />

            </div>

            <button
              type="submit"
              disabled={checkingQuotation}
              className="btn-primary w-full justify-center py-4 mt-4 disabled:opacity-50"
            >

              {checkingQuotation ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Checking...
                </>
              ) : (
                <>
                  <Search className="w-4 h-4" />
                  Check My Quotation
                </>
              )}

            </button>

          </form>

          {/* QUOTATION RESULT */}

          {quotation && (
            <div className="mt-8 max-w-xl mx-auto rounded-3xl bg-white/5 border border-white/10 p-6">

              <div className="flex items-center gap-3 mb-5">

                <div className="w-11 h-11 rounded-2xl bg-[#B8E14A]/15 flex items-center justify-center">

                  <FileText className="w-5 h-5 text-[#B8E14A]" />

                </div>

                <div>

                  <h3 className="font-heading text-2xl">
                    Quotation Found
                  </h3>

                  <p className="text-sm text-white/50">
                    {quotation.event_type} ·{" "}
                    {quotation.guests} guests
                  </p>

                </div>

              </div>

              <div className="space-y-3">

                <div className="flex justify-between text-sm">

                  <span className="text-white/60">
                    Event Date
                  </span>

                  <span>
                    {quotation.event_date ||
                      "-"}
                  </span>

                </div>

                <div className="flex justify-between text-sm gap-4">

                  <span className="text-white/60">
                    Location
                  </span>

                  <span className="text-right">
                    {quotation.location ||
                      "-"}
                  </span>

                </div>

                <div className="border-t border-white/10 pt-3 mt-3">

                  <div className="flex justify-between">

                    <span className="text-white/60">
                      Total
                    </span>

                    <strong>
                      ₹
                      {totalAmount.toLocaleString(
                        "en-IN"
                      )}
                    </strong>

                  </div>

                  <div className="flex justify-between mt-2">

                    <span className="text-white/60">
                      Advance
                    </span>

                    <strong>
                      ₹
                      {advanceAmount.toLocaleString(
                        "en-IN"
                      )}
                    </strong>

                  </div>

                  <div className="flex justify-between mt-2">

                    <span className="text-white/60">
                      Balance
                    </span>

                    <strong>
                      ₹
                      {balanceAmount.toLocaleString(
                        "en-IN"
                      )}
                    </strong>

                  </div>

                </div>

              </div>

              <button
                type="button"
                onClick={openQuotation}
                className="btn-primary w-full justify-center py-4 mt-6"
              >

                <FileText className="w-4 h-4" />

                View Full Quotation

              </button>

              <button
                type="button"
                onClick={() =>
                  navigate(
                    `/catering/tracking/${quotation.id}`
                  )
                }
                className="w-full justify-center py-3 mt-3 rounded-2xl border border-white/10 bg-white/5 hover:bg-white/10 transition"
              >
                Track Catering Status
              </button>

            </div>
          )}

        </div>

      </section>

    </div>
  );
}

// =====================================================
// FIELD COMPONENT
// =====================================================

function Field({
  label,
  name,
  type = "text",
  value,
  onChange,
  placeholder,
  icon,
  min,
}) {
  return (
    <div>

      <label className="block text-xs uppercase tracking-widest text-white/60 mb-2">
        {label}
      </label>

      <div className="relative">

        {icon && (
          <div className="absolute left-4 top-1/2 -translate-y-1/2 text-white/50">
            {icon}
          </div>
        )}

        <input
          name={name}
          type={type}
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          min={min}
          className={`w-full rounded-2xl bg-white/10 border border-white/10 px-4 py-3.5 text-white placeholder:text-white/35 outline-none focus:border-[#B8E14A] ${
            icon ? "pl-11" : ""
          }`}
        />

      </div>

    </div>
  );
}

// =====================================================
// COORDINATE BOX
// =====================================================

function CoordinateBox({
  label,
  value,
}) {
  return (
    <div className="rounded-2xl bg-black/10 border border-white/10 p-4">

      <div className="text-[10px] uppercase tracking-widest text-white/40 mb-1">
        {label}
      </div>

      <div className="font-mono text-sm text-white break-all">
        {value}
      </div>

    </div>
  );
}